// A camera for computers. On a phone, a file input with `capture` opens the
// phone's own camera; a laptop ignores `capture` and shows a file picker. So
// where the pointer is a mouse or trackpad and the browser can reach a camera,
// clicking the input's label opens a live view instead (a <dialog>), and
// "Capture" puts the frame into the input as a photo and fires `change`, just
// as a phone would. When the camera can't be opened (no camera, or access
// refused) the file picker opens after all.
import { Controller } from "@hotwired/stimulus";

const JPEG_QUALITY = 0.92;

export default class CameraController extends Controller<HTMLElement> {
  static override targets = ["input", "dialog", "video", "capture"];

  declare readonly inputTarget: HTMLInputElement;
  declare readonly dialogTarget: HTMLDialogElement;
  declare readonly videoTarget: HTMLVideoElement;
  declare readonly captureTarget: HTMLButtonElement;

  private stream: MediaStream | null = null;

  override disconnect(): void {
    this.stop();
  }

  async open(event: Event): Promise<void> {
    if (!liveCameraAvailable()) return;

    event.preventDefault();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
    } catch {
      this.inputTarget.click();
      return;
    }

    this.videoTarget.srcObject = this.stream;
    this.captureTarget.disabled = true;
    this.dialogTarget.showModal();
  }

  // The video has a picture to take.
  ready(): void {
    this.captureTarget.disabled = false;
  }

  capture(): void {
    const video = this.videoTarget;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const photos = new DataTransfer();
        photos.items.add(new File([blob], `business-card-${Date.now()}.jpg`, { type: "image/jpeg" }));
        this.inputTarget.files = photos.files;
        this.close();
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      },
      "image/jpeg",
      JPEG_QUALITY,
    );
  }

  close(): void {
    this.stop();
    if (this.dialogTarget.open) this.dialogTarget.close();
  }

  // Turns the camera (and its light) off, however the dialog was closed.
  stop(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.videoTarget.srcObject = null;
  }
}

function liveCameraAvailable(): boolean {
  return window.matchMedia("(pointer: fine)").matches && typeof navigator.mediaDevices?.getUserMedia === "function";
}
